import api from './api';
import feedback from '@components/ui/general/feedback';

/**
 * MetadataApi - Handles fetching game metadata and rules from backend
 * 
 * Game metadata includes:
 * - Stat constraints (min/max values)
 * - Game rules and configuration
 * - Combat formulas
 * - Effect stacking rules
 */

/**
 * Fetch game metadata including stat constraints and rules
 * @returns {Promise<object>} The metadata object with stat_constraints, game_rules, etc.
 * @throws {Error} If metadata cannot be fetched (critical error)
 */
export const getGameMetadata = async () => {
  try {
    const response = await api.get('/api/game/metadata');
    
    if (!response.data) {
      throw new Error('No metadata received from server');
    }
    
    //console.info('[MetadataApi] Game metadata loaded successfully', response.data);
    return response.data;
    
  } catch (error) {
    // This is a critical error - game cannot function without metadata
    console.error('[CRITICAL] Failed to fetch game metadata!', error);
    console.error('Game rules unavailable. Cannot continue.');
    
    // Show user-facing error
    feedback.error('Failed to load game rules. Please refresh the page.');
    
    // Re-throw the error so calling code knows it failed
    throw error;
  }
};

/**
 * Validate that metadata has required structure
 * @param {object} metadata - The metadata object to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const validateMetadata = (metadata) => {
  if (!metadata) {
    console.error('[MetadataApi] Metadata is null or undefined');
    return false;
  }

  // Validate stat_constraints
  if (!metadata.stat_constraints) {
    console.error('[MetadataApi] Missing stat_constraints in metadata');
    return false;
  }

  // Check that critical stats have constraints defined
  const requiredStats = ['hp', 'ap', 'armor', 'strength', 'will'];
  for (const stat of requiredStats) {
    if (!metadata.stat_constraints[stat]) {
      console.error(`[MetadataApi] Missing constraint for required stat: ${stat}`);
      return false;
    }
  }

  // Validate effects array
  if (!metadata.effects) {
    console.error('[MetadataApi] Missing effects array in metadata');
    return false;
  }

  if (!Array.isArray(metadata.effects)) {
    console.error('[MetadataApi] metadata.effects is not an array');
    return false;
  }

  // Validate each effect has critical fields
  for (const effect of metadata.effects) {
    const missingFields = [];

    if (!effect.id) missingFields.push('id');
    if (!effect.slug) missingFields.push('slug');
    if (!effect.name) missingFields.push('name');
    if (!effect.category) missingFields.push('category');
    if (!effect.category_color) missingFields.push('category_color');
    if (effect.properties === undefined) missingFields.push('properties');

    if (missingFields.length > 0) {
      console.error(
        `[MetadataApi] Effect ${effect.id || 'unknown'} (${effect.slug || 'unknown'}) is missing critical fields:`,
        missingFields
      );
      return false;
    }
  }

  // Validate game_config
  if (!metadata.game_config) {
    console.error('[MetadataApi] Missing game_config in metadata');
    return false;
  }

  // Validate tracked_stats
  if (!metadata.tracked_stats || !Array.isArray(metadata.tracked_stats)) {
    console.error('[MetadataApi] Missing or invalid tracked_stats in metadata');
    return false;
  }

  // Validate unit_templates
  if (!metadata.unit_templates || !Array.isArray(metadata.unit_templates)) {
    console.error('[MetadataApi] Missing or invalid unit_templates in metadata');
    return false;
  }

  // Validate each unit template has critical fields
  for (const template of metadata.unit_templates) {
    if (!template.slug) {
      console.error('[MetadataApi] Unit template missing slug:', template);
      return false;
    }
    if (!template.name) {
      console.error('[MetadataApi] Unit template missing name:', template);
      return false;
    }
  }

  return true;
};

/**
 * Get constraint for a specific stat
 * @param {object} metadata - The metadata object
 * @param {string} stat - The stat name
 * @returns {object} Constraint object with min/max values
 */
export const getStatConstraint = (metadata, stat) => {
  if (!metadata?.stat_constraints?.[stat]) {
    console.warn(`[MetadataApi] No constraint found for stat: ${stat}`);
    return { min: null, max: null };
  }
  
  return metadata.stat_constraints[stat];
};

/**
 * Apply constraint to a value
 * @param {number} value - The value to constrain
 * @param {object} constraint - The constraint object with min/max
 * @returns {number} The constrained value
 */
export const applyConstraint = (value, constraint) => {
  if (!constraint) return value;
  
  let result = value;
  
  // Apply minimum constraint
  if (constraint.min !== null && constraint.min !== undefined) {
    result = Math.max(constraint.min, result);
  }
  
  // Apply maximum constraint
  if (constraint.max !== null && constraint.max !== undefined) {
    result = Math.min(constraint.max, result);
  }
  
  return result;
};